import React from 'react';
import { BadgeCheckIcon, CheckIcon } from 'lucide-react';
import { StatusPill } from '../ui/StatusPill';
import type { ModuleId } from '../../types/content';
export function ModuleScene({
  id
}: {
  id: ModuleId;
}) {
  switch (id) {
    case 'knowledge':
      return <KnowledgeScene data-id="element-297" data-mp="2jrxg368iv2qd" />;
    case 'capability':
      return <CapabilityScene data-id="element-298" data-mp="2fpkdgqp2nkxt" />;
    case 'services':
      return <ServicesScene data-id="element-299" data-mp="1ryx1nuc1cb5z" />;
    default:
      return <CredentialsScene data-id="element-300" data-mp="1rzyeo4cm37qc" />;
  }
}
function KnowledgeScene() {
  const chain = [{
    label: 'Source',
    value: 'Hendricks et al., 2023'
  }, {
    label: 'Passage',
    value: 'Results · p. 6'
  }, {
    label: 'Term',
    value: 'Sleep fragmentation (governed)'
  }, {
    label: 'Finding',
    value: 'Approved · v14'
  }];
  return <div data-id="element-301" data-mp="1vyyuddzslu94">
      <p className="text-xs font-medium text-ink-muted" data-id="element-302" data-mp="1w2mmc8pla8lq">Provenance of a finding</p>
      <ol className="mt-4 space-y-2" data-id="element-303" data-mp="3cd73o6vjy72t">
        {chain.map((c, i) => <li key={c.label} className={`flex items-center justify-between rounded-xl px-3.5 py-3 text-[13px] ${i === chain.length - 1 ? 'bg-white shadow-glow' : 'bg-white/70 ring-1 ring-ink/[0.05]'}`} data-id="element-304" data-mp="37ggm1f43n3fa" data-mpi={i}>
            <span className="text-ink-muted" data-id="element-305" data-mp="3g4zgiintwhvc">{c.label}</span>
            <span className="font-medium" data-id="element-306" data-mp="3l53yye8armo7">{c.value}</span>
          </li>)}
      </ol>
    </div>;
}
function CapabilityScene() {
  const goals = [{
    name: 'Data reasoning',
    level: 72
  }, {
    name: 'Written communication',
    level: 54
  }, {
    name: 'Systems thinking',
    level: 38
  }];
  return <div data-id="element-307" data-mp="2xf9m3wnz9y3y">
      <div className="flex items-center justify-between" data-id="element-308" data-mp="2xezwwbarbuzb">
        <p className="text-xs font-medium text-ink-muted" data-id="element-309" data-mp="2xdghemwxdnyl">Development plan · S. Ortega</p>
        <StatusPill tone="accent" data-id="element-310" data-mp="32db6ckx543h0">Reassess in 6 wks</StatusPill>
      </div>
      <ul className="mt-5 space-y-4" data-id="element-311" data-mp="3764bvb0fnfu5">
        {goals.map(g => <li key={g.name} data-id="element-312" data-mp="29qgnfsfz1c5n">
            <div className="flex justify-between text-[13px]" data-id="element-313" data-mp="29p5j9rim62e0">
              <span className="font-medium" data-id="element-314" data-mp="2eorujvevt8k6">{g.name}</span>
              <span className="tabular-nums text-ink-muted" data-id="element-315" data-mp="2en6zm4onnnls">{g.level}%</span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-ink/[0.06]" data-id="element-316" data-mp="26miz69nrkvtd">
              <div className="h-full rounded-full bg-accent" style={{
            width: `${g.level}%`
          }} data-id="element-317" data-mp="22lpc1ii5bl1j" />
            </div>
          </li>)}
      </ul>
      <p className="mt-5 rounded-xl bg-white/70 px-3.5 py-3 text-[13px] text-ink-soft ring-1 ring-ink/[0.05]" data-id="element-318" data-mp="2gfzszdnwiax1">
        Next step: <span className="font-medium text-ink" data-id="element-319" data-mp="1np7xkientqar">Present a two-page analysis brief to a non-specialist panel.</span>
      </p>
    </div>;
}
function ServicesScene() {
  const steps = [{
    label: 'Request received',
    meta: 'Citizen Workspace',
    done: true
  }, {
    label: 'Case reviewed',
    meta: 'J. Moreno',
    done: true
  }, {
    label: 'Referral issued',
    meta: 'Housing assistance',
    done: true
  }, {
    label: 'Follow-up',
    meta: 'Due in 7 days',
    done: false
  }];
  return <div data-id="element-320" data-mp="394nk3pvbzuii">
      <div className="flex items-center justify-between" data-id="element-321" data-mp="343q15b2hywfy">
        <p className="text-xs font-medium text-ink-muted" data-id="element-322" data-mp="3437udki7kzts">Case NC-1042 · Housing support</p>
        <StatusPill tone="ok" data-id="element-323" data-mp="pv6x71pdp3vk">Referred</StatusPill>
      </div>
      <ol className="mt-5 space-y-3" data-id="element-324" data-mp="uvkz020dzhl5">
        {steps.map(s => <li key={s.label} className="flex items-center gap-3 text-[13px]" data-id="element-325" data-mp="usp0ockmmqw6">
            <span className={`flex h-6 w-6 items-center justify-center rounded-full ${s.done ? 'bg-ok-soft text-ok-ink' : 'bg-white ring-1 ring-ink/10'}`} data-id="element-326" data-mp="gx4sab2ixr30">
              {s.done ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-327" data-mp="19jplzi6h2f9q" /> : <span className="h-1.5 w-1.5 rounded-full bg-warn" data-id="element-328" data-mp="19mbt5gpgasrf" />}
            </span>
            <span className="flex-1 font-medium" data-id="element-329" data-mp="1dnnh7hsof5xi">{s.label}</span>
            <span className="text-ink-muted" data-id="element-330" data-mp="ztd11ouqwk32">{s.meta}</span>
          </li>)}
      </ol>
    </div>;
}
function CredentialsScene() {
  return <div data-id="element-331" data-mp="1sitrwi2pvsx6">
      <p className="text-xs font-medium text-ink-muted" data-id="element-332" data-mp="1sj3h43fxtw1t">Credential record</p>
      <div className="mt-4 rounded-2xl bg-white p-5 shadow-glow" data-id="element-333" data-mp="1sgz2aqgi3cmr">
        <div className="flex items-start justify-between gap-4" data-id="element-334" data-mp="1xb3crjoz62sl">
          <div data-id="element-335" data-mp="8o8tp049n3rc">
            <p className="text-base font-semibold tracking-tight" data-id="element-336" data-mp="8os7c4mvv5zk">Applied Data Reasoning</p>
            <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-337" data-mp="3nl1wc87qiw2">Demonstrated capability · Level 2</p>
          </div>
          <BadgeCheckIcon className="h-6 w-6 text-accent" aria-hidden="true" data-id="element-338" data-mp="3vt86au251pc2" />
        </div>
        <dl className="mt-5 grid grid-cols-2 gap-3 text-[13px]" data-id="element-339" data-mp="3qudnvt8322sh">
          {[['Holder', 'S. Ortega (sample)'], ['Issued', '14 Sep 2026'], ['Evidence', '2 assessed work samples'], ['Issuer', 'Sample Employer Ltd.']].map(([k, v]) => <div key={k} data-id="element-340" data-mp="ikvbth57jgpo">
              <dt className="text-[11px] text-ink-muted" data-id="element-341" data-mp="ik2dn4kbdzj5">{k}</dt>
              <dd className="font-medium" data-id="element-342" data-mp="dnlllz9zv10s">{v}</dd>
            </div>)}
        </dl>
      </div>
    </div>;
}